import { useCallback, useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import * as Library from "../../../utils/Library";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import { POSSetupSettingsMenuComponent } from "./StoresPage";
import BtnLargeSolid from "../../common/buttons/BtnLargeSolid";
import ContainerLoader from "../../common/loaders/ContainerLoader";
import ListComponent, { StatusBadge } from "../../common/lists/ListComponent";
import { useQueryClient } from "react-query";
import FormField from "../../common/forms/FormField";
import { AppQueryClient } from "../../../api/queryClient";
import PopupContext from "../../../PopupContext";
import AppContext from "../../../AppContext";
import POSChecklistPopup from "./POSPagesComponents/POSChecklistPopup";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.pos-store-page {
        display: flex;
        flex-direction: column;
    }

    .frame-313375 {
        border-radius: 4px;
        border-style: solid;
        border-color: var(--backgrounds-and-lines-n-100, #3c485a);
        border-width: 1px;
        padding: 4px 8px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: flex-start;
        justify-content: flex-start;
        flex-shrink: 0;
        width: 75px;
        position: relative;
    }

    .reorders {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: center;
        font-size: 11px !important;
        font-weight: 400;
        position: relative;
        align-self: stretch;
    }

    .store-id-form-field .inputFieldContainer {
        height: 26px;
    }
`;
// #endregion

// #region STATUS BADGE COLORS
const STATUS_BADGE_COLORS = {
    connected: "var(--text-icons-blue-600)",
    connecting: "var(--text-icons-green-500)",
    disconnected: "var(--text-icons-red-500)"
};

const disconnectButtonEnabled = false;
// #endregion

export default function POSStore() {
    document.title = `${APP_TITLE} - POS Stores`;

    // #region VARS
    const queryClient = useQueryClient();
    const { isLoadingApp } = useContext(AppContext);
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setGenericConfirmationPopup } = useContext(PopupContext);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [isConnectingPOSID, setIsConnectingPOSID] = useState(undefined);
    const [isDisconnectingPOSID, setIsDisconnectingPOSID] = useState(undefined);

    //----- Header Filters -----
    //const [searchText, setSearchText] = useState("");
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [allPOSList, setAllPOSList] = useState([]);
    const [selectedRetailBrand, setSelectedRetailBrand] = useState(null);
    const [filteredPOS, setFilteredPOS] = useState(undefined);
    const [requestsEnabled, setRequestsEnabled] = useState(false);

    //----- List Component Props -----
    const [listNumRows, setListNumRows] = useState(50);
    const [listComponentIsLoading, setListComponentIsLoading] = useState(false);
    const filters = {
        //filterRetailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        filterRetailBrandIDs: selectedRetailBrand ? [selectedRetailBrand?.value] : [],
        filterPOSIDs: filteredPOS?.map(item => item?.value)
        //searchText: searchText
    };
    const listQueryKey = ["POSStorePageList", filters];

    //----- Store Connect ID -----
    const [storeConnectIDs, setStoreConnectIDs] = useState({}); // { [storeID]: storeConnectID }
    const [connectButtonEnabled, setConnectButtonEnabled] = useState({}); // { [storeID]: boolean }

    const [listData, setListData] = useState(undefined);

    const [showChecklistPopup, setShowChecklistPopup] = useState(false);
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Store Name",
            selector: row => row?.name ?? "--"
            //width: "266px"
            //disableTooltip: true
            //tooltipContent: row => row?.productName || "--"
        },
        {
            name: "City, Country",
            selector: row => `${row?.city || "--"}, ${row?.country || "--"}`
            //center: true
            //width: "126px"
        },
        {
            name: "Store status",
            selector: row => (
                <StatusBadge
                    statusValue={row?.status || "--"}
                    colorValue={row?.status === "Active" ? "var(--text-icons-blue-600)" : "var(--text-icons-red-500)"}
                />
            )
            //width: "65px",
        },
        {
            name: "Pickup",
            selector: row => (
                <span
                    style={{
                        color: row?.pickup === true ? "var(--text-icons-green-500)" : "var(--text-icons-red-500)"
                    }}>
                    {row?.pickup === true ? "Yes" : row?.pickup === false ? "No" : "--"}
                </span>
            ),
            center: true,
            width: "80px"
        },
        {
            name: "Courier",
            selector: row => (
                <span
                    style={{
                        color: row?.courier === true ? "var(--text-icons-green-500)" : "var(--text-icons-red-500)"
                    }}>
                    {row?.courier === true ? "Yes" : row?.courier === false ? "No" : "--"}
                </span>
            ),
            center: true,
            width: "80px"
        },
        {
            name: "Shipping",
            selector: row => (
                <span
                    style={{
                        color: row?.shipping === true ? "var(--text-icons-green-500)" : "var(--text-icons-red-500)"
                    }}>
                    {row?.shipping === true ? "Yes" : row?.shipping === false ? "No" : "--"}
                </span>
            ),
            center: true,
            width: "80px"
        },
        {
            name: <span style={{ margin: "0 auto" }}>Open/Closed</span>,
            selector: row => (
                <span
                    style={{
                        color: row?.openNow === true ? "var(--text-icons-green-500)" : "var(--text-icons-red-500)"
                    }}>
                    {row?.openNow === true ? "Open" : row?.openNow === false ? "Closed" : "--"}
                </span>
            ),
            width: "150px",
            center: true
        },
        {
            name: "POS System",
            selector: row => row?.POSsystem || "--",
            width: "140px"
        },
        {
            name: "POS Status",
            selector: row => (
                <StatusBadge
                    statusValue={getStatus(row) || "--"}
                    colorValue={
                        getStatus(row) === "Connected"
                            ? STATUS_BADGE_COLORS.connected
                            : getStatus(row) === "Connecting..."
                              ? STATUS_BADGE_COLORS.connecting
                              : STATUS_BADGE_COLORS.disconnected
                    }
                />
            ),
            width: "120px"
        },
        {
            name: "Store ID",
            disableTooltip: true,
            selector: row => (
                <FormField
                    className="store-id-form-field"
                    value={storeConnectIDs[row?.id] || ""}
                    style={{ width: "120px", height: "32px" }}
                    disableAssistiveText={true}
                    onTextChange={text => onStoreIDTextChange(text, row)}
                    allowAutoComplete={false}
                    //if store is connected, lock the field
                    requiredType={getStatus(row) === "Connected" ? "locked" : ""}
                    disableTooltip={true}
                />
            ),
            width: "140px",
            center: true
        },
        {
            name: <span style={{ margin: "0 auto", paddingRight: "20px" }}>Action</span>,
            width: "270px",
            center: true,
            disableTooltip: true,
            selector: row => (
                <div style={{ display: "flex", gap: "8px" }}>
                    <div className="frame-313375">
                        <div
                            className={`reorders ${
                                connectButtonEnabled[row?.id] &&
                                getStatus(row) !== "Connecting..." &&
                                getStatus(row) !== "Connected"
                                    ? "text-active cursor-pointer"
                                    : "text-inactive cursor-not-allowed"
                            }`}
                            onClick={() => {
                                if (getStatus(row) !== "Connecting..." && getStatus(row) !== "Connected") {
                                    showConnectStorePopup(row);
                                }
                            }}>
                            Connect
                        </div>
                    </div>
                    <div className="frame-313375">
                        <div className="reorders text-inactive cursor-not-allowed">Suspend</div>
                    </div>
                    <div className="frame-313375">
                        <div
                            className={`reorders ${
                                getStatus(row) === "Connected" &&
                                disconnectButtonEnabled &&
                                getStatus(row) !== "Disconnecting..."
                                    ? "text-active cursor-pointer"
                                    : "text-inactive cursor-not-allowed"
                            }`}
                            onClick={() => {
                                if (disconnectButtonEnabled && getStatus(row) !== "Disconnecting...")
                                    disconnectPOSRequest(row);
                            }}>
                            Disconnect
                        </div>
                    </div>
                </div>
            )
        }
    ];
    // #endregion

    // #region POPUPS
    const showConnectStorePopup = rowStore => {
        if (!connectButtonEnabled[rowStore?.id]) return;

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            headingText: "JUST CHECKING",
            headingColor: "var(--text-icons-blue-500)",
            title: "Connecting a store?",
            messageHtml: `You are about to connect ${rowStore?.name || "--"} POS`,
            confirmText: "CONNECT",
            cancelText: "CANCEL",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
                connectPOSRequest(rowStore, storeConnectIDs[rowStore?.id]);
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            },
            confirmState: "secondary"
        });
    };
    // #endregion

    // #region METHODS
    const onRetailBrandChange = selection => {
        setSelectedRetailBrand(selection);
        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrandValue =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map(item => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }

                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrandValue
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrandValue) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setSelectedRetailBrand(defaultRetailBrandObj);
        }

        if (data?.posSystems?.length) {
            const formFields = data?.posSystems?.map(item => ({
                ...item,
                name: item.name,
                value: item._id,
                selected: true
            }));

            setAllPOSList(formFields);
            setFilteredPOS(formFields);
        }

        setRequestsEnabled(true);
    };

    const onRefreshClicked = async () => {
        AppQueryClient.invalidateQueries(listQueryKey);

        //setStoreConnectIDs({});
        setConnectButtonEnabled({});
    };

    const onSelectedPOSChanged = (selections, dropdownDataWithSelections) => {
        setFilteredPOS(selections);
        setAllPOSList(dropdownDataWithSelections);
    };

    const onViewChecklistClicked = () => {
        setShowChecklistPopup(true);
    };

    const onStoreIDTextChange = (text, row) => {
        setStoreConnectIDs(prev => ({
            ...prev,
            [row?.id]: text
        }));

        setConnectButtonEnabled(prev => ({
            ...prev,
            [row?.id]: text ? true : false
        }));
    };
    // #endregion

    // #region REQUESTS
    // eslint-disable-next-line no-unused-vars
    const disconnectPOSRequest = async rowStore => {
        if (!disconnectButtonEnabled) return;

        const storeID = rowStore?.id;
        if (!storeID) {
            Library.showToastMessage({
                message: "Store ID is required",
                type: "error"
            });
            return;
        }

        if (getStatus(rowStore) !== "Connected") return;

        setIsDisconnectingPOSID(storeID);

        const postData = { storeID };

        const res = await Library.makeApiRequest({
            endpoint: "X/Stores/disconnectStorePOS",
            data: postData
        });

        setIsDisconnectingPOSID(undefined);
        if (res?.data?.status !== 200) return;
        //onRefreshClicked();

        setListData(prev =>
            prev.map(item => {
                if (item.id === storeID) {
                    return { ...item, POSstatus: "Not connected" };
                }
                return item;
            })
        );
    };

    const connectPOSRequest = async (rowStore, storeConnectIDString) => {
        const storeID = rowStore?.id;

        if (!storeID) {
            Library.showToastMessage({
                message: "Store ID is required",
                type: "error"
            });
            return;
        }

        if (!storeConnectIDString) {
            Library.showToastMessage({
                message: "Store Connect ID is required",
                type: "error"
            });
            return;
        }

        if (getStatus(rowStore) === "Connected") return;

        setIsConnectingPOSID(storeID);

        const connectFields = { StoreId: storeConnectIDString };
        const postData = { storeID, connectFields };

        const res = await Library.makeApiRequest({
            endpoint: "X/Stores/connectStorePOS",
            data: postData
            //setLoader: setIsLoading
        });

        setIsConnectingPOSID(undefined);
        if (res?.data?.status !== 200) return;
        //onRefreshClicked();

        setListData(prev =>
            prev.map(item => {
                if (item.id === storeID) {
                    return { ...item, POSstatus: "Connected" };
                }
                return item;
            })
        );
    };

    const mapListData = data => {
        /*const listData = data?.stores || [];
        const storeConnectIDs = {};
        listData.forEach(item => {
            storeConnectIDs[item.id] = item.storeConnectID || "";
        });
        setStoreConnectIDs(storeConnectIDs);
        return listData;*/

        const { result: listDataArr } = data;

        const storeConnectIDs = {};
        const connectButtonEnabled = {};
        listDataArr.forEach(item => {
            storeConnectIDs[item.id] = item?.POSconnectFields?.StoreId || "";
            connectButtonEnabled[item.id] = false;
        });
        setStoreConnectIDs(storeConnectIDs);
        setConnectButtonEnabled(connectButtonEnabled);

        setListData(listDataArr);

        return data;
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setupHeaders({
            props,
            allRetailBrandsList,
            onRetailBrandChange,
            onFiltersLoaded,
            selectedRetailBrand,
            allPOSList,
            onSelectedPOSChanged,
            onRefreshClicked
        });
    }, [allRetailBrandsList, selectedRetailBrand, allPOSList]);

    useEffect(() => {
        Library.removeQueries(queryClient, listQueryKey);
    }, []);

    const getStatus = useCallback(
        rowStore => {
            if (isConnectingPOSID === rowStore?.id) {
                return "Connecting...";
            } else if (isDisconnectingPOSID === rowStore?.id) {
                return "Disconnecting...";
            }
            return rowStore?.POSstatus;
        },
        [isConnectingPOSID, isDisconnectingPOSID]
    );
    // #endregion

    // #region RENDER
    return (
        <StyledPage className="pos-store-page">
            <FullScreenOverlay show={showChecklistPopup}>
                <POSChecklistPopup onClose={() => setShowChecklistPopup(false)} />
            </FullScreenOverlay>

            <ContainerLoader
                isLoading={!isLoadingApp && listComponentIsLoading}
                className="w-100"
                isLoadingOverlay={true}
                addBlurFilters={false}>
                <ListComponent
                    className="w-100"
                    tableClassName="expandedRowsCentered"
                    title="Stores"
                    columns={listColumns}
                    api={{
                        endpoint: "getStoresData",
                        useCommonPostRequest: true,
                        commonContext: "Stores",
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            limit: listNumRows,
                            ...filters
                        },
                        onSuccess: mapListData
                    }}
                    rightCardTopComponent={
                        <BtnLargeSolid
                            text="View Checklist"
                            onClick={onViewChecklistClicked}
                            disabled={false}
                            normalColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                            hoverColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                            activeColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                            disabledColor="var(--Buttons-All-Platforms-Gray-500, #10141b)"
                            textStyle={{ fontSize: "12px", textTransform: "none" }}
                            style={{
                                marginRight: "0px",
                                height: "25px",
                                width: "95px",
                                padding: "6px "
                            }}
                        />
                    }
                    selectableRows={true}
                    showLoader={false}
                    onListLoading={loading => setListComponentIsLoading(loading)}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    requestEnabled={requestsEnabled}
                    mappedTempData={listData}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            </ContainerLoader>
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders({
    props,
    allRetailBrandsList,
    onRetailBrandChange,
    onFiltersLoaded,
    selectedRetailBrand,
    allPOSList,
    onSelectedPOSChanged,
    onRefreshClicked
}) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={false} placeholder="Search" /* onTextChange={onSearch} */ /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/stores", active: false, disabled: false, title: "Stores" },
        { to: "/stores/staff", active: false, disabled: true, title: "Staff" },
        { to: "#", active: true, disabled: false, title: "POS" }
    ]);
    props.setControlBarRightBtns([
        { to: "/stores/pos", active: false, disabled: false, title: "Config", icon: "passcode-lock" },
        { to: "#", active: true, disabled: false, title: "Store", icon: "monitor" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "SAVE",
                        enabled: false,
                        onClick: undefined
                    },
                    gridEnabled: false,
                    listEnabled: false
                }}
                onRefreshClick={onRefreshClicked}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allRetailBrandsList,
                              onDropdownChange: onRetailBrandChange,
                              disabled: false,
                              type: "singleSelect",
                              selectedValue: selectedRetailBrand?.name
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onSelectedPOSChanged,
                    disabled: false,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all POS",
                        labelAllSelected: "All POS",
                        labelNoneSelected: "--",
                        allItems: allPOSList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        usePOSSystems: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true,
                    enabled: false,
                    active: false,
                    outlined: false,
                    layoutComponent: <POSSetupSettingsMenuComponent />
                }}
            />
        )
    });
}
// #endregion

// #region SMALL COMPONENTS

// #endregion
